import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
  STORAGE_KEYS,
} from "../../helpers";
import { showError, showToast } from "../../constants/toasts";
import * as Yup from "yup";
import { useFormik } from "formik";
import { usePostLoginMutation, useSendOtpMutation } from "../../service/auth";
import { useAppDispatch } from "../../hooks/store";
import { setCredentials } from "../../reducers/authSlice";
import Loader from "../../constants/Loader";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const [loginMutation] = usePostLoginMutation();
  const [sendOtp] = useSendOtpMutation();
  const dispatch = useAppDispatch();

  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [valueTabs, setValueTabs] = useState<number>(0);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  });

  const handleChangePhone = (phone: any, country: any) => {
    phoneFormik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    phoneFormik.setFieldValue("countryCode", country?.dialCode);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address"
        ),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      const body = {
        email: values.email,
        password: values.password,
      };

      try {
        setLoading(true);
        // const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await loginMutation(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast("Logged In Successfully.");
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          setToStorage(STORAGE_KEYS.userData, JSON.stringify(response?.data));
          dispatch(
            setCredentials({
              user: response?.data || null,
              token: response?.data?.token || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          if (response?.data?.isProfileComplete) {
            navigate("/dashboard");
          } else {
            if (response?.data?.vendor?.step) {
              navigate("/subscription-form");
            } else {
              navigate("/signup-form");
            }
          }
        } else {
          setLoading(false);
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const phoneFormik = useFormik({
    initialValues: {
      phone: "",
      countryCode: "+971",
    },
    validationSchema: Yup.object({
      phone: Yup.string().required("Phone is required"),
    }),
    onSubmit: async (values: any) => {
      phoneFormik.setSubmitting(true);
      const body = {
        phone: values.phone,
        countryCode: "+" + values.countryCode,
        otpType: 2,
      };

      try {
        setLoading(true);
        // const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await sendOtp(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast("OTP Sent Successfully.");

          navigate("/otp?type=phoneLogin", {
            state: {
              phone: phoneFormik.values.phone,
              countryCode: phoneFormik.values.countryCode,
            },
          });
        } else {
          setLoading(false);
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <Loader isLoad={loading} />
      <div className="authBox">
        <h2>Login</h2>
        <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="view product tabs"
            className="custom_tabs_links"
          >
            <Tab label="Email" {...a11yProps(0)} />
            <Tab label="Phone Number" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" onSubmit={formik.handleSubmit}>
            {/* <div className="control_group">
              <label htmlFor="email">Select Store</label>
              <Select
                fullWidth
                value={Status}
                onChange={handleStatusChange} >
                <MenuItem disabled value="default">Select Store</MenuItem>
                <MenuItem value="All">Store1</MenuItem>
                <MenuItem value="Active">Store2 </MenuItem>
                <MenuItem value="Suspended">Store3</MenuItem>
              </Select>
            </div> */}
            <div className="control_group">
              <label htmlFor="email">Email Address</label>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="Enter Email Address"
                className="text_field"
                fullWidth
                value={formik.values.email}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.email &&
                  typeof formik.errors.email === "string"
                    ? formik.errors.email
                    : ""
                }
              />
            </div>

            <div className="control_group">
              <label htmlFor="password">Password</label>
              <TextField
                id="password"
                name="password"
                placeholder="Enter Password"
                className="text_field"
                fullWidth
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.password &&
                  typeof formik.errors.password === "string"
                    ? formik.errors.password
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group d_flex">
              <div className="remember_box">
                <FormControlLabel
                  control={<Checkbox />}
                  checked={formik.values.remember}
                  name="remember"
                  onChange={formik.handleChange}
                  label="Remember me"
                />
              </div>
              <button
                type="button"
                className="anchor_link"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot Password?
              </button>
            </div>

            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary w_100"
                variant="contained"
              >
                Login
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <form className="form" onSubmit={phoneFormik.handleSubmit}>
            <div className="control_group ">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                enableSearch
                value={
                  phoneFormik.values.countryCode + phoneFormik.values.phone
                }
                country={"uae"}
                inputClass="phoneInput"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              <p className="err_msg">
                {error && !phoneFormik.values.phone?.length
                  ? "Phone number is required"
                  : ""}
              </p>
            </div>

            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary w_100"
                variant="contained"
                onClick={() => setError(true)}
              >
                Login
              </Button>
            </div>
          </form>
        </CustomTabPanel>

        {/* <div className="sign_log">
          <p>
            Don't have an account?{" "}
            <a onClick={() => navigate("/sign-up")}>Sign up</a>
          </p>
        </div> */}
      </div>
    </AuthLayout>
  );
};

export default Login;
